import React, { useCallback, useRef, useState, useEffect } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Divider, Modal } from '@material-ui/core';
import { IoIosArrowBack } from 'react-icons/io';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import Select, { SelectOptionType } from '../../newComponents/Select';
import Button from '../../newComponents/Button';
import Switch from '../../newComponents/Switch';

import * as S from './styles';

interface TerminalFormData {
  deviceCode: string;
  serieNfce?: string;
  serieContingencia?: string;
  cnpjEmitente?: string;
}

const AMBIENTE_OPTIONS: SelectOptionType[] = [
  { value: 1, label: 'Produção' },
  { value: 2, label: 'Homologação' },
];

const EditTerminalFiscal: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ambiente, setAmbiente] = useState<SelectOptionType>(
    AMBIENTE_OPTIONS[1],
  );
  const [proximoNumero, setProximoNumero] = useState<number>(0);
  const [emissaoHabilitada, setEmissaoHabilitada] = useState(true);
  // Ajuste da numeração: rascunho separado do valor vigente, para o modal poder
  // confrontar os dois ("de X para Y") e o cancelamento não deixar resíduo.
  const [novoProximoNumero, setNovoProximoNumero] = useState<string>('');
  const [isNumeracaoModalOpen, setIsNumeracaoModalOpen] = useState(false);
  const [isAjustandoNumeracao, setIsAjustandoNumeracao] = useState(false);

  useEffect(() => {
    async function loadData(): Promise<void> {
      try {
        const response = await api.get(
          `companies/${user.empresa_id}/terminais-fiscais/${id}`,
        );

        const terminal = response.data;
        setProximoNumero(terminal.proximoNumero ?? 0);
        setEmissaoHabilitada(terminal.emissaoHabilitada !== false);
        setAmbiente(
          AMBIENTE_OPTIONS.find((o) => o.value === terminal.ambiente) ??
            AMBIENTE_OPTIONS[1],
        );
        formRef.current?.setData({
          deviceCode: terminal.deviceCode ?? '',
          serieNfce: String(terminal.serieNfce ?? ''),
          serieContingencia: String(terminal.serieContingencia ?? ''),
          cnpjEmitente: terminal.cnpjEmitente ?? '',
        });
      } catch (err) {
        addToast({ type: 'error', title: 'Erro ao carregar terminal' });
        history.push('/terminais-fiscais');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [id, user.empresa_id, addToast, history]);

  const handleSubmit = useCallback(
    async (data: TerminalFormData) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          deviceCode: Yup.string().required('Informe o device code'),
          serieNfce: Yup.string().matches(
            /^\d*$/,
            'Série deve ser numérica',
          ),
          serieContingencia: Yup.string().matches(
            /^\d*$/,
            'Série de contingência deve ser numérica',
          ),
          cnpjEmitente: Yup.string()
            .transform((value) => (value === '' ? undefined : value))
            .notRequired()
            .matches(/^\d{14}$/, 'CNPJ deve conter 14 dígitos'),
        });

        await schema.validate(data, { abortEarly: false });

        // proximo_numero é espelho de leitura (B8) e NÃO é enviado no update.
        const payload = {
          deviceCode: data.deviceCode,
          serieNfce: data.serieNfce ? Number(data.serieNfce) : undefined,
          serieContingencia: data.serieContingencia
            ? Number(data.serieContingencia)
            : undefined,
          ambiente: ambiente.value,
          cnpjEmitente: data.cnpjEmitente || null,
          emissaoHabilitada,
        };

        await api.patch(
          `companies/${user.empresa_id}/terminais-fiscais/${id}`,
          payload,
        );

        addToast({ type: 'success', title: 'Terminal fiscal atualizado' });
        history.push('/terminais-fiscais');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        } else {
          addToast({
            type: 'error',
            title: 'Erro ao atualizar terminal fiscal',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [id, ambiente, emissaoHabilitada, user.empresa_id, addToast, history],
  );

  const handleAbrirAjusteNumeracao = useCallback(() => {
    setNovoProximoNumero(String(proximoNumero || 1));
    setIsNumeracaoModalOpen(true);
  }, [proximoNumero]);

  const numeroInformado = Number(novoProximoNumero);
  // O piso real é do servidor (ele conhece o que a série consumiu); aqui só se
  // evita o ida-e-volta óbvio de mandar um número que já sabemos ser inválido.
  const isNumeroValido =
    Number.isInteger(numeroInformado) &&
    numeroInformado >= 1 &&
    numeroInformado !== proximoNumero;

  const handleConfirmarAjusteNumeracao = useCallback(async () => {
    if (!isNumeroValido) return;

    setIsAjustandoNumeracao(true);
    try {
      const response = await api.patch(
        `companies/${user.empresa_id}/terminais-fiscais/${id}/numeracao`,
        { proximoNumero: numeroInformado },
      );

      setProximoNumero(response.data?.proximoNumero ?? numeroInformado);
      setIsNumeracaoModalOpen(false);
      addToast({
        type: 'success',
        title: 'Numeração ajustada',
        description: `A próxima NFC-e deste terminal sairá com o nº ${numeroInformado}.`,
      });
    } catch (err) {
      // A recusa de "só avançar" vem do servidor com o último número emitido —
      // é o que o operador precisa ler para escolher outro valor, então o modal
      // fica aberto com a mensagem.
      const mensagem = (err as {
        response?: { data?: { message?: string } };
      })?.response?.data?.message;

      addToast({
        type: 'error',
        title: 'Não foi possível ajustar a numeração',
        description:
          mensagem ?? 'Tente novamente ou verifique a conexão com a emissão.',
      });
    } finally {
      setIsAjustandoNumeracao(false);
    }
  }, [addToast, id, isNumeroValido, numeroInformado, user.empresa_id]);


  return (
    <S.Container>
      <header>
        <button
          className="previous_page"
          type="button"
          onClick={() => history.push('/terminais-fiscais')}
        >
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <div>
          <h1>Editar Terminal</h1>
          <p>PDV físico com série e numeração próprias para emissão de NFC-e.</p>
        </div>
      </header>

      <Divider />

      <S.CustomForm ref={formRef} onSubmit={handleSubmit}>
        <Input
          name="deviceCode"
          label="Device Code:"
          placeholder="Ex.: CAIXA-01"
          disabled={isLoading}
        />

        <div className="multi">
          <Input name="serieNfce" label="Série NFC-e:" placeholder="Ex.: 1" />
          <Input
            name="serieContingencia"
            label="Série de contingência:"
            placeholder="Ex.: 899"
          />
        </div>

        <div className="multi">
          <div className="field">
            <label htmlFor="ambiente-select">Ambiente:</label>
            <Select
              id="ambiente-select"
              options={AMBIENTE_OPTIONS}
              value={ambiente}
              onChange={(option: any) => setAmbiente(option as SelectOptionType)}
            />
          </div>
          <Input
            name="cnpjEmitente"
            label="CNPJ emitente (opcional):"
            placeholder="14 dígitos, sem máscara"
          />
        </div>

        <div className="field">
          <label htmlFor="emissao-habilitada">Emitir NFC-e neste terminal:</label>
          <div className="switch-field">
            <Switch
              id="emissao-habilitada"
              checked={emissaoHabilitada}
              onChange={(value: boolean) => setEmissaoHabilitada(value)}
              disabled={isLoading}
            />
            <span>{emissaoHabilitada ? 'Emite' : 'Não emite'}</span>
          </div>
          <span className="field-note">
            Desligado, as vendas deste caixa são registradas normalmente mas não
            geram NFC-e. A emissão só acontece quando a empresa E o terminal
            estiverem habilitados.
          </span>
        </div>

        <div className="field">
          <label htmlFor="proximo-numero">Próximo número:</label>
          <div className="numeracao-field">
            <div id="proximo-numero" className="readonly-field">
              {proximoNumero}
            </div>
            <Button
              variant="outlined"
              type="button"
              onClick={handleAbrirAjusteNumeracao}
              disabled={isLoading}
            >
              Alterar
            </Button>
          </div>
          <span className="field-note">
            {proximoNumero > 1
              ? `Última NFC-e emitida neste terminal: nº ${proximoNumero - 1}.`
              : 'Nenhuma NFC-e emitida neste terminal ainda.'}{' '}
            A numeração é controlada pela emissão (atac-notas) e avança sozinha
            a cada nota. Só altere para saltar uma faixa inutilizada ou assumir
            uma sequência já usada fora do sistema.
          </span>
        </div>

        <div className="buttons">
          <Button full type="submit" loading={isLoading} disabled={isLoading}>
            Salvar
          </Button>
        </div>
      </S.CustomForm>

      <Modal
        open={isNumeracaoModalOpen}
        onClose={() => setIsNumeracaoModalOpen(false)}
      >
        <S.ModalContent>
          <h1>Alterar o próximo número?</h1>
          <p>
            A numeração da NFC-e é sequencial e fiscalizada. Alterar à mão só é
            necessário para saltar números inutilizados ou assumir uma sequência
            já emitida fora do sistema.
          </p>

          <div className="numeracao-modal-field">
            <label htmlFor="novo-proximo-numero">
              Número da próxima NFC-e
            </label>
            <input
              id="novo-proximo-numero"
              type="number"
              min={1}
              value={novoProximoNumero}
              onChange={(e) => setNovoProximoNumero(e.target.value)}
              disabled={isAjustandoNumeracao}
            />
            <span className="numeracao-modal-note">
              Atual: {proximoNumero}. Só é possível AVANÇAR — repetir um número
              já emitido faz a SEFAZ rejeitar a nota por duplicidade.
            </span>
          </div>

          <div className="modal-actions">
            <Button
              variant="outlined"
              full
              type="button"
              onClick={() => setIsNumeracaoModalOpen(false)}
              disabled={isAjustandoNumeracao}
            >
              Cancelar
            </Button>
            <Button
              full
              type="button"
              onClick={handleConfirmarAjusteNumeracao}
              loading={isAjustandoNumeracao}
              disabled={!isNumeroValido || isAjustandoNumeracao}
            >
              Confirmar
            </Button>
          </div>
        </S.ModalContent>
      </Modal>
    </S.Container>
  );
};

export default EditTerminalFiscal;
